import {
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
  type Dispatch,
  type FormEvent,
  type SetStateAction,
  type ReactNode,
  useCallback,
} from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
  type PaymentRequest,
  type StripeCardElement,
  type StripeCardElementChangeEvent,
} from '@stripe/stripe-js';
import { useHistory } from 'react-router-dom';
import { Item } from 'react-stately';
import { fullPageRedirect } from '@hungryroot/environment';
import { useExperiment } from '@hungryroot/experiments';
import { CalendarOutline, Question, Warning } from '@hungryroot/icons';
import { log } from '@hungryroot/o11y';
import { useGetOrder } from '@hungryroot/order-details';
import {
  states,
  usePlaceCheckoutOrder,
  type CustomerAddress,
} from '@hungryroot/pages-checkout';
import { Tooltip } from '@hungryroot/pho';
import {
  Heading,
  Paragraph,
  Select,
  TextInput,
  Checkbox,
} from '@hungryroot/ramen';
import { funnelAnalytics } from '@hungryroot/segment';
import { saveCard } from '../../../../frontend/react/src/api/checkout';
import {
  CONGRATS_PATH,
  SHIPPING_PATH,
  PATHS,
} from '../../../../frontend/react/src/constants/checkout';
import { getOrderTotalCost, useOptions } from '../../pages/Payment/utils';
import { Disclaimer } from '../Disclaimer/Disclaimer';
import { PaymentRequestButton } from '../PaymentRequestButton/PaymentRequestButton';
import styles from './PaymentForm.module.css';

type CreditCardErrors = {
  ccName: string | undefined;
  ccNumber: string;
  zipcode: string | undefined;
  generalError: string;
};

interface Props {
  testid?: string;
  orderId: number;
  setLoading: Dispatch<SetStateAction<boolean>>;
  loading: boolean;
  orderTotal: string;
  subtotal: number;
  zipcode?: string | undefined;
  address: CustomerAddress;
  customerId?: number;
  plan?: number | null;
  checkoutButton?: () => ReactNode;
}

export function PaymentForm(props: Props) {
  const {
    testid,
    orderId,
    loading,
    setLoading,
    orderTotal,
    subtotal,
    zipcode,
    address,
    customerId,
    plan,
    checkoutButton,
  } = props;
  const placeCheckoutOrder = usePlaceCheckoutOrder();
  const options = useOptions();
  const history = useHistory();
  const [ccName, setCCName] = useState('');
  const [errors, setErrors] = useState<CreditCardErrors>({
    ccName: undefined,
    ccNumber: '',
    zipcode: undefined,
    generalError: '',
  });
  const [showCCError, setShowCCError] = useState(false);
  const [ccEmpty, setCCEmpty] = useState(true);
  const [processingError, setProcessingError] = useState('');
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
    null
  );
  const [ftcConsent, setFtcConsent] = useState<boolean | undefined>(undefined);
  const [ftcConsentRequiredError, setFtcConsentRequiredError] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const ftcCheckboxRef = useRef<HTMLInputElement>(null);

  const stripe = useStripe();
  const elements = useElements();
  const [isZipInputShown, setIsZipInputShown] = useState(false);
  const [zipInput, setZipInput] = useState('');
  const [billingState, setBillingState] = useState('');

  const orderTotalCost = getOrderTotalCost(orderTotal);
  const [paymentHidden, setPaymentHidden] = useState(orderTotalCost === 0);
  const noPayment = orderTotalCost === 0 && ccEmpty;

  const ftcRegulationVariant = useExperiment(168);
  const isInFtcRegulationVariant =
    ftcRegulationVariant !== undefined && ftcRegulationVariant > 0;

  const analytics = funnelAnalytics();
  const { data: firstOrder } = useGetOrder(orderId);

  const paymentReqOptions = {
    country: 'US',
    currency: 'usd',
    total: {
      label: 'total',
      amount: orderTotalCost,
      pending: false,
    },
  };

  useEffect(() => {
    (async () => {
      if (stripe) {
        const paymentReq = stripe.paymentRequest(paymentReqOptions);
        const result = await paymentReq.canMakePayment();
        if (result) {
          setPaymentRequest(paymentReq);
        }
      }
    })();
  }, [stripe]);

  useLayoutEffect(() => {
    if (containerRef.current === null) {
      return;
    }

    if (processingError || errors.generalError) {
      containerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [processingError, errors.generalError]);

  const updateCCStates = (event: StripeCardElementChangeEvent) => {
    setShowCCError(false);
    setCCEmpty(false);
    if (event.error || !event.complete) {
      event.error && event.error.message
        ? setErrors({ ...errors, ccNumber: event.error.message })
        : setErrors({ ...errors, ccNumber: 'Enter credit card information' });
      return;
    }
    setErrors({ ...errors, ccNumber: '' });
  };

  const placeOrderAndRedirect = useCallback(() => {
    setLoading(true);

    const path = '/api/place_checkout_order/';
    const errorData = {
      customerId,
      orderId,
      address,
      path,
    };

    if (plan !== undefined && plan !== null) {
      // Customer already has plan. Redirect without calling placeOrder.
      log('v1_place_checkout_order', {
        ...errorData,
        error: 'customer plan already exists',
      });
      fullPageRedirect(PATHS[CONGRATS_PATH]);
    }

    placeCheckoutOrder.mutate(
      { orderId, consented: ftcConsent },
      {
        onSuccess: (data) => {
          if (data['success']) {
            customerId !== undefined &&
              firstOrder !== undefined &&
              analytics.orderCompleted(customerId, firstOrder);

            fullPageRedirect(PATHS[CONGRATS_PATH]);
          } else if (data['already_has_plan']) {
            // We check for plan before calling placeOrder. This error state is included as a fallback given a difficult to reproduce bug
            log('v1_place_checkout_order', {
              ...errorData,
              error: 'already_has_plan',
            });

            fullPageRedirect(PATHS[CONGRATS_PATH]);
          } else if (data['card_decline']) {
            setLoading(false);
            setProcessingError('Sorry the credit card was declined');

            log('v1_place_checkout_order', {
              ...errorData,
              error: 'card_decline',
            });
          } else {
            setLoading(false);
            setProcessingError('Sorry something went wrong. Please try again.');

            log('v1_place_checkout_order', {
              ...errorData,
              error: `Error processing payment: ${data}`,
            });
          }
        },
        onError: (error) => {
          setLoading(false);
          setErrors({
            ...errors,
            generalError: 'Sorry something went wrong. Please try again.',
          });

          log('v1_place_checkout_order', { ...errorData, error });
        },
      }
    );
  }, [
    ftcConsent,
    setLoading,
    customerId,
    orderId,
    address,
    plan,
    placeCheckoutOrder,
    setProcessingError,
    setErrors,
    errors,
    firstOrder,
    analytics,
  ]);

  const handlePaymentSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const invalidZip = isZipInputShown && zipInput.length !== 5;
    const showErrors =
      ((!ccName || errors.ccNumber || ccEmpty || invalidZip) && !noPayment) ||
      (!ftcConsent && isInFtcRegulationVariant);
    const path = '/api/save_payment/';
    const dataDogData = {
      customerId,
      orderId,
      address,
      path,
    };

    if (showErrors) {
      const ccNameError = !ccName ? 'Please fill out this field' : undefined;
      const ccNumberError =
        ccEmpty && !errors.ccNumber
          ? 'Enter credit card information'
          : errors.ccNumber;
      const zipError = invalidZip ? 'Enter valid zip code' : undefined;
      setErrors({
        ...errors,
        ccName: ccNameError,
        ccNumber: ccNumberError,
        zipcode: zipError,
      });
      (errors.ccNumber || ccEmpty) && setShowCCError(true);

      if (!ftcConsent && isInFtcRegulationVariant) {
        ftcCheckboxRef.current?.scrollIntoView({ behavior: 'smooth' });
        setFtcConsentRequiredError(true);
      }
      return;
    }

    if (!elements || !stripe || !orderId || !history) {
      return;
    }
    setLoading(true);
    setProcessingError('');
    setErrors({
      ...errors,
      generalError: '',
      ccNumber: '',
      ccName: undefined,
      zipcode: undefined,
    });
    setShowCCError(false);

    if (!noPayment) {
      const addressCountry = 'US';
      const billingZipcode = zipInput ? zipInput : zipcode;
      const result = await stripe.createToken(
        elements.getElement(CardElement) as StripeCardElement,
        {
          name: ccName,
          address_country: addressCountry,
          address_zip: billingZipcode,
        }
      );

      if (result.error) {
        setErrors({ ...errors, ccNumber: result.error.message as string });
        setShowCCError(true);
        setLoading(false);
        return;
      }

      const data = {
        stripe_token: result.token.id,
        order_id: orderId,
        method_name: 'basic-card',
      };
      const cardSaveResult = await saveCard(data);

      if ('stale_order' in cardSaveResult && cardSaveResult['stale_order']) {
        log('v1_save_payment_stale_order', {
          ...dataDogData,
          error: 'stale_order',
        });
        history.push(PATHS[SHIPPING_PATH]);
        return;
      }

      if (cardSaveResult['error_msg']) {
        log('v1_save_payment_error', {
          ...dataDogData,
          error: cardSaveResult['error_msg'],
        });
        setLoading(false);
        setErrors({ ...errors, ccNumber: cardSaveResult['error_msg'] });
        setShowCCError(true);
        return;
      }
    }

    placeOrderAndRedirect();
  };

  const setCCNameAndErrors = (name: string) => {
    setCCName(name);
    // remove error when customer types
    setErrors({ ...errors, ccName: undefined, ccNumber: '' });
  };

  const ftcCheckbox = (
    <Checkbox
      className={styles['ftc-consent']}
      checkboxClassName={styles['ftc-checkbox']}
      value="true"
      aria-invalid={Boolean(ftcConsent)}
      aria-describedby="terms-error"
      isChecked={ftcConsent}
      onChange={(val) => {
        setFtcConsent(val);
      }}
      label={
        <div ref={ftcCheckboxRef}>
          I agree that I will be charged the auto-renew price of ${subtotal}
          {'  '}per weekly delivery following my discount, plus applicable
          taxes. I understand that my weekly price may change depending on my
          order or personalized recommendations from Hungryroot based on my
          ordering history. I can skip a delivery, modify my order, or cancel my
          subscription on my Account page or by contacting{' '}
          <a href="mailto:help@hungryroot.com" className={styles.link}>
            help@hungryroot.com
          </a>{' '}
          by 7:00 pm ET on my Renewal Date, found on my Account page.
          <br />
          <br />
          By placing my order, I agree to Hungryroot's{' '}
          <a href="/terms/" target="_blank" className={styles.link}>
            Terms of Service
          </a>{' '}
          &{' '}
          <a href="/privacy/" target="_blank" className={styles.link}>
            Privacy Policy
          </a>
          .
        </div>
      }
      testid={`${testid}-ftc-consent`}
    />
  );

  return (
    <div ref={containerRef}>
      <Heading variant="H4">Payment</Heading>
      {processingError || errors.generalError ? (
        <Paragraph className={styles.error}>
          {processingError || errors.generalError}
        </Paragraph>
      ) : null}
      {orderTotalCost === 0 ? (
        <Checkbox
          value="true"
          isChecked={!paymentHidden}
          onChange={() => {
            setPaymentHidden(!paymentHidden);
          }}
          label="Add credit card (optional)"
          testid={`${testid}-payment-checkbox`}
          className={styles['payment-checkbox']}
        />
      ) : null}
      {!paymentHidden ? (
        <div className={styles['payment-request-button-container']}>
          <PaymentRequestButton
            isInvalid={isInFtcRegulationVariant && !ftcConsent}
            showDisclaimer={!isInFtcRegulationVariant}
            orderId={orderId}
            setErrorMessage={(error: string) =>
              setErrors({ ...errors, generalError: error })
            }
            loading={loading}
            placeOrder={placeOrderAndRedirect}
            onClick={() => {
              if (!ftcConsent && isInFtcRegulationVariant) {
                setFtcConsentRequiredError(true);
                ftcCheckboxRef.current?.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            orderTotal={orderTotal}
            subtotal={subtotal}
            billingState={isZipInputShown ? billingState : ''}
          />
        </div>
      ) : null}
      <form
        data-testid={testid}
        className={styles.form}
        onSubmit={handlePaymentSubmit}
        id="payment-submit"
      >
        {!paymentHidden ? (
          <>
            <div className={styles.row} data-testid="cc-name">
              <TextInput
                label="Name on card"
                errorMessage={errors.ccName}
                className={styles.item}
                name="name"
                value={ccName}
                onChange={(value) => setCCNameAndErrors(value)}
              />
            </div>
            <div className={styles['credit-container']}>
              <div
                className={`${styles.credit} ${
                  showCCError
                    ? styles['credit-error']
                    : styles['credit-default']
                }`}
              >
                <CardElement
                  options={options}
                  id="payment-card-number"
                  className={styles['stripe-input']}
                  onChange={(e) => updateCCStates(e)}
                />
                <Tooltip
                  content="3-digit security code usually found on the back of your card. American Express cards have a 4-digit code located on the front."
                  containerClassName={styles.tooltip}
                >
                  <Question />
                </Tooltip>
              </div>

              {(errors.ccNumber || ccEmpty) && showCCError && (
                <Paragraph
                  variant="caption"
                  className={styles['cc-error']}
                  testid="cc-error"
                >
                  {errors.ccNumber}
                </Paragraph>
              )}
            </div>
            <Checkbox
              value="true"
              isChecked={!isZipInputShown}
              onChange={() => {
                setIsZipInputShown(!isZipInputShown);
              }}
              label="Billing zip code is the same as delivery address"
              testid={`${testid}-zip-checkbox`}
            />
            {isZipInputShown && (
              <div
                className={styles['billing-container']}
                data-testid={`${testid}-billing-zip`}
              >
                <Select
                  testid={`${testid}-select`}
                  label="State"
                  items={states}
                  className={styles.item}
                  selectedKey={billingState}
                  onSelectionChange={(key) => {
                    setBillingState(key as string);
                  }}
                  name="state"
                >
                  {(item) => (
                    <Item key={item.abbreviation}>{item.abbreviation}</Item>
                  )}
                </Select>
                <TextInput
                  isZipcode
                  label="Zip code"
                  value={zipInput}
                  className={styles.item}
                  name="zip_code"
                  onChange={(value: string) => {
                    setZipInput(value);
                    setErrors({ ...errors, zipcode: undefined });
                  }}
                  errorMessage={errors.zipcode}
                  maxLength={5}
                />
              </div>
            )}
          </>
        ) : null}
      </form>
      {!isInFtcRegulationVariant && paymentRequest === null && (
        <Disclaimer
          // keep two decimals places if subtotal is not a whole number
          planTotal={subtotal.toFixed(subtotal % 1 !== 0 ? 2 : 0)}
          billingState={isZipInputShown ? billingState : ''}
          className={styles.disclaimer}
          testid="disclaimer"
        />
      )}
      {ftcConsentRequiredError && (
        <Paragraph className={styles['ftc-error']} role="alert">
          <Warning className={styles.warning} />
          Please accept the terms & conditions to continue
        </Paragraph>
      )}
      {isInFtcRegulationVariant && ftcCheckbox}
      {checkoutButton?.()}
      <div className={styles.tip}>
        <CalendarOutline />
        <Paragraph>Skip a week or cancel anytime.</Paragraph>
      </div>
    </div>
  );
}
